import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, type Theme, Typography, alpha } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { toast } from "@stores/toastStore";
import {
  type ParsedMarkdownTable,
  downloadCsv,
  markdownTableToCsv,
} from "@utils/bomParse";
import { nowFilename } from "@utils/formatters";
import { useMemo } from "react";

interface BomExportButtonProps {
  content: string;
}

export function BomExportButton({ content }: BomExportButtonProps) {
  const handleExport = () => {
    try {
      const csv = markdownTableToCsv(content);
      const filename = `bom_${nowFilename()}.csv`;
      downloadCsv(csv, filename);
      toast.success(`Exported ${filename}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "BOM export failed");
    }
  };

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<DownloadIcon fontSize="small" />}
      onClick={handleExport}
    >
      Export BOM as CSV
    </Button>
  );
}

// ── Column-type heuristics ───────────────────────────────────────────────────
// BOMs come from the LLM as markdown, so column names vary ("Ref", "Reference",
// "Designators"; "Qty", "Quantity"; "MPN", "Part Number"). Classify by header
// keyword so we can apply the right alignment, font, and wrapping policy
// without needing the agent to emit a fixed schema.
type ColumnKind = "designator" | "quantity" | "mono" | "prose";

const DESIGNATOR_HEADER = /\b(ref|designator|refdes|symbol)/i;
const QUANTITY_HEADER = /\b(qty|quantity|count|#)\b/i;
const MONO_HEADER =
  /\b(mpn|part\s*number|sku|lcsc|footprint|package|value|tolerance|voltage|rating|spec|code)\b/i;

function classifyColumn(header: string): ColumnKind {
  if (QUANTITY_HEADER.test(header)) return "quantity";
  if (DESIGNATOR_HEADER.test(header)) return "designator";
  if (MONO_HEADER.test(header)) return "mono";
  return "prose";
}

interface BomTableProps {
  table: ParsedMarkdownTable;
  /** Raw markdown content used for CSV export. */
  rawContent: string;
}

export function BomTable({ table, rawContent }: BomTableProps) {
  const columnKinds = useMemo(
    () => table.header.map(classifyColumn),
    [table.header],
  );

  // Stable, content-derived keys. Computed up-front so the JSX `.map()`
  // callbacks don't reference array indices — keeps lint happy and is
  // just as cheap as the index path because the rows are immutable for
  // the lifetime of this BOM render.
  const keyedRows = useMemo(
    () =>
      table.rows.map((row, idx) => ({
        row,
        key: `${idx}${row.join("")}`,
      })),
    [table.rows],
  );
  const headerKeys = useMemo(
    () => table.header.map((label, idx) => `${idx}${label}`),
    [table.header],
  );

  const totalLines = table.rows.length;
  const totalQuantity = useMemo(() => {
    const qtyIdx = columnKinds.findIndex((k) => k === "quantity");
    if (qtyIdx === -1) return null;
    let sum = 0;
    let parsedAny = false;
    for (const row of table.rows) {
      const n = Number.parseInt(row[qtyIdx]?.replace(/[^\d-]/g, "") ?? "", 10);
      if (Number.isFinite(n)) {
        sum += n;
        parsedAny = true;
      }
    }
    return parsedAny ? sum : null;
  }, [columnKinds, table.rows]);

  return (
    <Box
      sx={{
        my: 1.5,
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: 2,
        backgroundColor: "surface.raised",
        overflow: "hidden",
        // Editorial amber leading edge — the brand cue for "AI-generated".
        // 2px inset so it doesn't shift the card's outer edge.
        boxShadow: (t) =>
          `inset 2px 0 0 ${alpha(t.palette.editorial.main, 0.7)}`,
      }}
    >
      {/* ── Header band ───────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          px: 1.75,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "border.default",
          backgroundColor: (t) => alpha(t.palette.editorial.main, 0.04),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
          <Typography
            variant="overline"
            sx={{
              color: "editorial.contrastText",
              fontFamily: (t) => t.fonts.mono,
              fontSize: "0.65rem",
              letterSpacing: "0.18em",
              lineHeight: 1,
            }}
          >
            Bill of Materials
          </Typography>
          <Typography
            sx={{
              fontFamily: (t) => t.fonts.mono,
              fontSize: "0.7rem",
              color: "text.secondary",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {totalLines} {totalLines === 1 ? "line" : "lines"}
            {totalQuantity !== null && totalQuantity !== totalLines
              ? ` · ${totalQuantity} parts`
              : ""}
          </Typography>
        </Box>
      </Box>

      {/* ── Scroll viewport ───────────────────────────────────────────── */}
      {/* Horizontal-only scroll. No maxHeight — the chat is the vertical
          scroll container; nesting one inside it creates the double-scroll
          trap that motivated this rewrite. */}
      <Box
        sx={{
          overflowX: "auto",
          // Native scrollbar styling — match the global theme palette so
          // a horizontal scrollbar at the bottom of the BOM doesn't read
          // as a Windows-95 grey bar against the dark surface.
          "&::-webkit-scrollbar": { height: 8 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: (t) => t.palette.border.strong,
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: (t) => alpha(t.palette.editorial.main, 0.6),
          },
        }}
      >
        <Box
          component="table"
          sx={tableSx}
          // Force a meaningful minimum so on narrow viewports the table
          // scrolls horizontally instead of squashing engineering cells
          // into illegible 2-character columns.
          style={{ minWidth: Math.max(560, table.header.length * 110) }}
        >
          <Box component="thead" sx={{ display: "table-header-group" }}>
            <Box component="tr">
              {table.header.map((label, idx) => {
                const align: "right" | undefined =
                  columnKinds[idx] === "quantity" ? "right" : undefined;
                return (
                  <Box
                    component="th"
                    key={headerKeys[idx]}
                    sx={[headerCellSx, align ? { textAlign: align } : null]}
                  >
                    {label}
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Box component="tbody">
            {keyedRows.map(({ row, key: rowKey }) => (
              <Box component="tr" key={rowKey} sx={bodyRowSx}>
                {row.map((cell, colIdx) => {
                  const kind = columnKinds[colIdx] ?? "prose";
                  return (
                    <Box
                      component="td"
                      key={headerKeys[colIdx] ?? `_col_${cell}`}
                      sx={[bodyCellSx, cellKindSx[kind]]}
                    >
                      {/* Empty cell → render an em-dash so the row
                          doesn't visually collapse. */}
                      {cell || (
                        <Box component="span" sx={{ color: "text.disabled" }}>
                          —
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          px: 1.25,
          py: 0.75,
          borderTop: "1px solid",
          borderColor: "border.default",
          backgroundColor: "surface.default",
        }}
      >
        <BomExportButton content={rawContent} />
      </Box>
    </Box>
  );
}

// ── Style constants ──────────────────────────────────────────────────────────
// Hoisted out of the component so identity is stable across renders and
// the `sx` array merging in the component stays cheap.

const tableSx: SystemStyleObject<Theme> = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  fontSize: "0.8125rem",
  // Auto layout (the default) so columns size to content. We constrain the
  // table's outer min-width via `style` rather than per-cell to keep the
  // header/body alignment in sync — that was the bug under the previous
  // `display: block` regime.
  tableLayout: "auto",
};

const headerCellSx: SystemStyleObject<Theme> = {
  px: 1.5,
  py: 0.85,
  textAlign: "left",
  fontFamily: (t: Theme) => t.fonts.mono,
  fontSize: "0.68rem",
  fontWeight: 600,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "text.secondary",
  whiteSpace: "nowrap",
  borderBottom: "1px solid",
  borderColor: "border.default",
  backgroundColor: "surface.raisedHover",
};

const bodyRowSx: SystemStyleObject<Theme> = {
  // Zebra: tint every other row in editorial-amber softer so the table
  // reads as stripes instead of a wall.
  "&:nth-of-type(even) td": {
    backgroundColor: (t: Theme) => alpha(t.palette.editorial.main, 0.025),
  },
  // Hover row — soft amber tint + 2px leading-edge accent on the first
  // cell only. Implemented as inset box-shadow so it doesn't shift the
  // cell's intrinsic width on hover.
  "&:hover td": {
    backgroundColor: (t: Theme) => alpha(t.palette.editorial.main, 0.08),
  },
  "&:hover td:first-of-type": {
    boxShadow: (t: Theme) => `inset 2px 0 0 ${t.palette.editorial.main}`,
  },
};

const bodyCellSx: SystemStyleObject<Theme> = {
  px: 1.5,
  py: 0.85,
  color: "text.primary",
  borderBottom: "1px solid",
  borderColor: "border.default",
  verticalAlign: "top",
  transition: (t: Theme) => `background-color ${t.motion.fast}`,
};

const cellKindSx: Record<ColumnKind, SystemStyleObject<Theme>> = {
  designator: {
    fontFamily: (t: Theme) => t.fonts.mono,
    fontSize: "0.78rem",
    color: "accent.contrastText",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  quantity: {
    fontFamily: (t: Theme) => t.fonts.mono,
    fontSize: "0.78rem",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: "text.primary",
  },
  mono: {
    fontFamily: (t: Theme) => t.fonts.mono,
    fontSize: "0.78rem",
    whiteSpace: "nowrap",
    color: "text.primary",
  },
  prose: {
    fontSize: "0.8125rem",
    color: "text.secondary",
    // Allow long descriptions to wrap rather than push the whole table
    // wider; long words still break so a single hyphenated description
    // can't escape its column.
    whiteSpace: "normal",
    wordBreak: "break-word",
    minWidth: 180,
    maxWidth: 360,
  },
};
